import React from 'react';
import { BookOpen, CalendarClock, ListChecks, CalendarDays, ArrowDownWideNarrow, PartyPopper, Sparkles } from 'lucide-react';

const WORKBOOK_PDF = 'https://customer-assets-7cd3h4nn.emergentagent.net/job_92af2a39-6ed7-4f8f-ab7f-04de2b246c6c/artifacts/bjhhvoa5__NTP_90_day_Launch_Accelerator_Workbook_.pdf';

const PHASES = [
  { tag: 'Days 1-30', title: 'Foundation', desc: 'Foundation building, planning, programs, and board development.', color: '#1D4ED8' },
  { tag: 'Days 31-60', title: 'Build & Brand', desc: 'Branding, marketing, pricing, operations, and launch preparation.', color: '#7C3AED' },
  { tag: 'Days 61-90', title: 'Grow & Amplify', desc: 'Marketing, content creation, audience engagement, and scaling.', color: '#DB2777' },
];

const STEPS = [
  {
    icon: CalendarClock,
    title: '1. Follow the Daily Structure',
    body: 'Each section is laid out in 30-day phases. Simply complete the daily items as listed - they are purposely short, focused actions that move your nonprofit forward one step at a time.',
    phases: true,
  },
  {
    icon: ListChecks,
    title: '2. Reference the Linked Templates & Checklists',
    body: 'Throughout the workbook you will see references such as "Day 12 - Board Manual Checklist", "Day 18 - Budget Template", and "Day 54 - Strategic Planning Guide". Use these as your done-for-you support system - they remove the guesswork and help you create high-quality organizational materials quickly. Every referenced template lives in your Document Library on the Accelerator tab.',
  },
  {
    icon: CalendarDays,
    title: '3. Set Weekly Work Times in Your Schedule',
    body: 'One of the first tasks is to add your work sessions to your calendar. Blocking the time keeps you committed and helps you build healthy nonprofit leadership habits.',
  },
  {
    icon: ArrowDownWideNarrow,
    title: '4. Work Through Each Section in Order',
    body: 'Each block builds on the last. Your mission clarity (Days 31-32) connects to your marketing plan (Days 45-49), which prepares you for content creation (Days 73-90). Skipping ahead may cause confusion - trust the process.',
  },
  {
    icon: PartyPopper,
    title: '5. Celebrate Your Progress',
    body: 'Mark each day complete as you go and watch your phase progress fill up. By Day 90 you will have a fully built, fundable, launch-ready nonprofit - so celebrate the milestones along the way.',
  },
];

export default function NonprofitGuide() {
  return (
    <div className="max-w-3xl" data-testid="npa-guide">
      {/* Hero */}
      <div className="rounded-2xl border border-[#E2E8F0] bg-gradient-to-br from-[#EFF6FF] to-white p-6 mb-6">
        <div className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-wider text-[#1D4ED8] mb-2"><BookOpen size={14} /> Workbook Guide</div>
        <h2 className="text-2xl font-black text-[#0A0F1C]">Nonprofits That Profit - 90 Day Launch Accelerator</h2>
        <p className="text-sm text-[#475569] mt-2">This workbook is your step-by-step companion through the 90-Day Launch Accelerator. It is designed to keep you organized, focused, and confident as you build or restructure your nonprofit using Samara Michelle's real-world strategies.</p>
        <div className="grid sm:grid-cols-2 gap-2 mt-4 text-sm text-[#334155]">
          <div className="flex items-start gap-2"><span className="text-[#1D4ED8] font-black">•</span> <span><strong>Daily roadmaps</strong> that break your first 90 days into manageable, actionable steps.</span></div>
          <div className="flex items-start gap-2"><span className="text-[#1D4ED8] font-black">•</span> <span><strong>Monthly productivity tips</strong> to keep you motivated and consistent.</span></div>
          <div className="flex items-start gap-2"><span className="text-[#1D4ED8] font-black">•</span> <span><strong>Program, board, budgeting, marketing & branding prompts</strong> for each phase of the journey.</span></div>
          <div className="flex items-start gap-2"><span className="text-[#1D4ED8] font-black">•</span> <span><strong>Linked assets & templates</strong> referenced throughout to help you implement faster.</span></div>
        </div>
        <p className="text-sm text-[#0A0F1C] font-semibold mt-4">This is not just a document - it is your blueprint for building a nonprofit that is organized, fundable, and ready to serve your community with excellence.</p>
        <a href={WORKBOOK_PDF} target="_blank" rel="noreferrer" data-testid="npa-guide-download" className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 text-xs font-bold bg-[#0A0F1C] text-white rounded-lg hover:opacity-90"><BookOpen size={13} /> Open the full workbook PDF</a>
      </div>

      {/* How to use */}
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={16} className="text-[#7C3AED]" />
        <h3 className="text-lg font-black text-[#0A0F1C]">How to Use This Workbook Effectively</h3>
      </div>
      <p className="text-sm text-[#64748B] mb-4">To get the most value out of your accelerator experience, use this workbook as an <em>implementation guide</em> - not just a note-taker.</p>

      <div className="space-y-3">
        {STEPS.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.title} className="rounded-2xl border border-[#E2E8F0] bg-white p-5" data-testid={`npa-guide-step-${s.title[0]}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#EFF6FF] text-[#1D4ED8]"><Icon size={16} /></span>
                <h4 className="text-base font-black text-[#0A0F1C]">{s.title}</h4>
              </div>
              <p className="text-sm text-[#475569] leading-relaxed">{s.body}</p>
              {s.phases && (
                <div className="grid sm:grid-cols-3 gap-2 mt-3">
                  {PHASES.map((p) => (
                    <div key={p.tag} className="rounded-xl border border-[#E2E8F0] p-3">
                      <div className="text-[10px] font-black uppercase tracking-wider" style={{ color: p.color }}>{p.tag}</div>
                      <div className="text-sm font-bold text-[#0A0F1C]">{p.title}</div>
                      <div className="text-[11px] text-[#64748B] mt-0.5">{p.desc}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 rounded-2xl border border-dashed border-[#CBD5E1] bg-[#F8FAFC] p-4 text-sm text-[#475569]">
        <strong className="text-[#0A0F1C]">Ready to start?</strong> Head to the <strong>90-Day Accelerator</strong> tab, open your project, and work Day 1 forward. Use <em>AI-draft</em> on any day for a ready-to-edit deliverable, and export a <em>Board Packet</em> or <em>90-Day Plan PDF</em> anytime to share your progress.
      </div>
    </div>
  );
}
